/**
 * RoeEditor — Paie › Relevés d'emploi › un RE : formulaire complet modifiable, recalcul depuis la paie, émission, PDF, fichier RE Web, n° de série, RE modifié. Batch #14.264.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Building2, User, CalendarDays, Coins, MessageSquareText, HandCoins, Save, RefreshCw, Send, FileText, FileCode2, Hash, FilePen, AlertCircle } from "lucide-react";
import { API, authH, Section, Btn, inputCls, err } from "./_shared";
import { RoeBadge } from "./RoeTile";
import { EmployerBlock, EmployeeBlock, DatesBlock, EarningsBlock, ReasonBlock, SeparationBlock } from "./roe/RoeBlocks";

export default function RoeEditor({ masterId, roeId }) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [f, setF] = useState(null);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState("");
  const [serial, setSerial] = useState("");
  const base = `${API}/admin/payroll/${masterId}/roe/${roeId}`;

  const load = useCallback(async () => {
    try { const r = await axios.get(base, { headers: authH() }); setDoc(r.data); setF(r.data.data); setSerial(r.data.serial_no || ""); } catch (e) { err(e); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const ro = doc?.status !== "draft";
  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); if (okMsg) toast.success(okMsg); return r; } catch (e) { err(e); } finally { setBusy(""); }
  };
  const save = () => run("save", async () => {
    const r = await axios.put(base, { data: f }, { headers: authH() });
    setDoc((d) => ({ ...d, ...r.data })); setF(r.data.data); setErrors(r.data.errors || []);
  }, "RE enregistré");
  const recompute = () => run("recompute", async () => {
    if (!window.confirm("Recalculer les blocs 10-12, 15A/15B/15C et 17A depuis la paie ? La raison, les observations et les blocs 17B/17C sont conservés.")) return;
    const r = await axios.post(`${base}/recompute`, {}, { headers: authH() }); setDoc((d) => ({ ...d, ...r.data })); setF(r.data.data); setErrors(r.data.errors || []);
  }, "Valeurs recalculées depuis la paie");
  const issue = () => run("issue", async () => {
    await axios.put(base, { data: f }, { headers: authH() });
    if (!window.confirm("Émettre ce relevé d'emploi ? Il ne sera plus modifiable (un RE modifié pourra être créé).")) return;
    await axios.post(`${base}/issue`, {}, { headers: authH() }); await load();
  }, "Relevé d'emploi émis");
  const dl = (kind, ext) => run(kind, async () => {
    const r = await axios.get(`${base}/${kind}`, { headers: authH(), responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `RE_${doc.employee_no || doc.employee_name}.${ext}`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000);
  }, null);
  const saveSerial = () => run("serial", async () => { await axios.put(`${base}/serial`, { serial_no: serial }, { headers: authH() }); await load(); }, "N° de série enregistré");
  const amend = () => run("amend", async () => { const r = await axios.post(`${base}/amend`, {}, { headers: authH() }); navigate(`/admin/paie/${masterId}/releves-emploi/${r.data.id}`); }, "Brouillon de RE modifié créé");

  if (!doc || !f) return <p className="text-sm text-slate-500 p-6">Chargement…</p>;
  const codes = doc.codes;
  return (
    <div className="space-y-4" data-testid="paie-roe-editor">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border rounded-xl p-4">
        <div>
          <div className="flex items-center gap-2"><h3 className="font-bold text-slate-900" data-testid="paie-roe-editor-title">Relevé d'emploi — {doc.employee_name}</h3><RoeBadge doc={doc} testId="paie-roe-editor-status" /></div>
          <p className="text-xs text-slate-500 mt-0.5">{doc.employee_no} · {doc.nb_stubs} bulletin(s) pris en compte · créé {String(doc.created_at).slice(0, 10)}{doc.issued_at ? ` · émis ${String(doc.issued_at).slice(0, 10)}` : ""}{doc.amends_serial ? ` · modifie le RE n° ${doc.amends_serial}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!ro && <Btn tone="slate" icon={RefreshCw} busy={busy === "recompute"} onClick={recompute} testId="paie-roe-recompute">Recalculer depuis la paie</Btn>}
          {!ro && <Btn tone="sky" icon={Save} busy={busy === "save"} onClick={save} testId="paie-roe-save">Enregistrer</Btn>}
          {!ro && <Btn tone="emerald" icon={Send} busy={busy === "issue"} onClick={issue} testId="paie-roe-issue">Émettre le RE</Btn>}
          <Btn tone="slate" icon={FileText} busy={busy === "pdf"} onClick={() => dl("pdf", "pdf")} testId="paie-roe-dl-pdf">PDF</Btn>
          <Btn tone="slate" icon={FileCode2} busy={busy === "xml"} onClick={() => dl("xml", "xml")} testId="paie-roe-dl-xml" title="Fichier d'extraction pour l'import sur RE Web">Fichier RE Web</Btn>
          {ro && <Btn tone="orange" icon={FilePen} busy={busy === "amend"} onClick={amend} testId="paie-roe-amend">Créer un RE modifié</Btn>}
        </div>
      </div>

      {doc.nb_stubs === 0 && !ro && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800" data-testid="paie-roe-no-stubs">Aucun bulletin pris en compte — aucune paie approuvée ou payée depuis le dernier RE émis pour cet employé. Les blocs 10-12 et 15 sont à compléter manuellement.</div>
      )}

      {errors.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800" data-testid="paie-roe-errors">
          <div className="font-bold flex items-center gap-1 mb-1"><AlertCircle className="w-3.5 h-3.5" /> À compléter avant l'émission</div>
          <ul className="list-disc ml-4 space-y-0.5">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      {ro && (
        <div className="bg-white border rounded-xl p-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="paie-roe-serial-card">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">1 N° de série attribué par Service Canada (après transmission RE Web)</label>
            <input className={inputCls} value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="ex. A12345678" data-testid="paie-roe-serial-input" />
          </div>
          <Btn tone="sky" icon={Hash} busy={busy === "serial"} onClick={saveSerial} testId="paie-roe-serial-save">Enregistrer le n° de série</Btn>
        </div>
      )}

      <fieldset disabled={ro} className={ro ? "opacity-80" : ""}>
        <div className="space-y-4">
          <Section title="Employeur (blocs 4 · 5 · 6 · 7)" icon={Building2} testId="paie-roe-block-employer"><EmployerBlock f={f} set={set} codes={codes} /></Section>
          <Section title="Employé (blocs 8 · 9 · 13)" icon={User} testId="paie-roe-block-employee"><EmployeeBlock f={f} set={set} /></Section>
          <Section title="Dates (blocs 10 · 11 · 12 · 14)" icon={CalendarDays} testId="paie-roe-block-dates"><DatesBlock f={f} set={set} codes={codes} /></Section>
          <Section title="Heures et rémunération assurables (blocs 15A · 15B · 15C)" icon={Coins} testId="paie-roe-block-earnings"><EarningsBlock f={f} set={set} computed={doc.computed} /></Section>
          <Section title="Sommes versées au départ (blocs 17A · 17B · 17C)" icon={HandCoins} testId="paie-roe-block-separation"><SeparationBlock f={f} set={set} codes={codes} /></Section>
          <Section title="Raison, personne-ressource et observations (blocs 16 · 18 · 20 · 21 · 22)" icon={MessageSquareText} testId="paie-roe-block-reason"><ReasonBlock f={f} set={set} codes={codes} /></Section>
        </div>
      </fieldset>
      <p className="text-[10px] text-slate-400">La transmission officielle se fait sur <b>RE Web</b> (Service Canada) : saisie manuelle à partir du PDF ou import du fichier d'extraction. Un RE doit être émis dans les 5 jours civils suivant l'arrêt de rémunération (ou selon la règle des 15 jours / fin de période pour les émetteurs électroniques).</p>
    </div>
  );
}
